import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, DataObject, RestartAlt, UploadOutlined } from '@mui/icons-material';
import {
  IMAGE_ALT_MAX,
  IMAGE_HEIGHT_DEFAULT,
  IMAGE_HEIGHT_MAX,
  IMAGE_HEIGHT_MIN,
  MARGIN_IDS,
  MARGIN_PRESETS,
  PAGE_NUMBER_TEXT,
  SLOT_POSITIONS,
  SLOT_TEXT_MAX,
  bandImageMaxHeight,
  bandsForPage,
  formatFieldDate,
  isAllowedImageUrl,
  normalizePageSetup,
  renderSlotText,
  resolvePageGeometry,
  serializePageSetup,
} from '../../utils/pageSetup';

/**
 * Page setup dialog: orientation, margins, header and footer bands (three slots each), a
 * different first page and a live mini preview. Edits a draft; Save hands the normalized page
 * setup to `onSave` (only when it changed) and the host marks the document or template dirty.
 *
 * Slots are None / Text / Image / Page number. "Page number" is a shortcut for a text slot of
 * `{page}` or `Page {page} of {pages}`. Text slots have an "Insert field" menu (live fields are
 * filled in the Pages view and exports). Images go through the editor's image pipeline
 * (imageService: type and size checks, resize, compression) and are then stored in Quill's
 * Supabase storage (`PAGE_SETUP_IMAGE_BUCKET`, public object URL), because page_setup only
 * accepts Quill storage URLs (`isAllowedImageUrl`; editor body images are inline data: URLs,
 * which page_setup refuses). A result the allowlist refuses is never kept.
 */

/** Live fields offered by "Insert field" (labels shown, tokens inserted). */
export const FIELD_OPTIONS = Object.freeze([
  { token: '{title}', label: 'Document title' },
  { token: '{customer}', label: 'Customer' },
  { token: '{project}', label: 'Project' },
  { token: '{date}', label: 'Date' },
  { token: '{page}', label: 'Page' },
  { token: '{pages}', label: 'Page count' },
]);

/** Page number shortcuts (stored as text slots). */
const PAGE_NUMBER_FORMATS = Object.freeze({ n: PAGE_NUMBER_TEXT.simple, nOfM: PAGE_NUMBER_TEXT.ofTotal });

/** Storage bucket (public) for header and footer images, objects under `<user id>/`. */
export const PAGE_SETUP_IMAGE_BUCKET = 'page-setup-images';

const MAX_TEXT = SLOT_TEXT_MAX;
const IMAGE_MIN = IMAGE_HEIGHT_MIN;
const IMAGE_MAX = IMAGE_HEIGHT_MAX;
const DEFAULT_IMAGE_HEIGHT = IMAGE_HEIGHT_DEFAULT;
const POSITIONS = SLOT_POSITIONS;
const POSITION_LABELS = { left: 'left', center: 'centre', right: 'right' };
const MARGIN_LABELS = { normal: 'Normal', narrow: 'Narrow', wide: 'Wide' };
const MARGIN_ORDER = MARGIN_IDS;
const BAND_LABELS = {
  header: 'Header',
  footer: 'Footer',
  firstHeader: 'First page header',
  firstFooter: 'First page footer',
};
const NONE = Object.freeze({ type: 'none' });

/** Hidden visually, read by screen readers. */
const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

/** Preview page: the larger side of the sheet is this many px. */
const PREVIEW_MAX = 230;

/**
 * Which editor a slot shows.
 * @param {object|null|undefined} slot - Slot.
 * @returns {'none'|'text'|'image'|'page'} Kind.
 */
export function slotKind(slot) {
  if (!slot || slot.type === 'none') return 'none';
  if (slot.type === 'image') return 'image';
  if (slot.type === 'text') {
    const text = String(slot.text || '').trim();
    if (text === PAGE_NUMBER_FORMATS.n || text === PAGE_NUMBER_FORMATS.nOfM) return 'page';
    return 'text';
  }
  return 'none';
}

/**
 * The band to edit (null bands, e.g. an unset first page header, are empty).
 * @param {object|null|undefined} band - Band.
 * @returns {{left: object, center: object, right: object}} Band.
 */
const bandOf = (band) => ({
  left: band?.left || NONE,
  center: band?.center || NONE,
  right: band?.right || NONE,
});

/**
 * The editable draft of a page setup (normalized; null = defaults).
 * @param {object|null} value - Page setup.
 * @returns {object} Normalized page setup.
 */
const toDraft = (value) => normalizePageSetup(value);

/**
 * Initial UI kind for every slot of every band.
 * @param {object} setup - Normalized page setup.
 * @returns {Record<string, string>} Kinds keyed `band.position`.
 */
const kindsOf = (setup) => {
  const kinds = {};
  Object.keys(BAND_LABELS).forEach((bandKey) => {
    const band = bandOf(setup?.[bandKey]);
    POSITIONS.forEach((pos) => {
      kinds[`${bandKey}.${pos}`] = slotKind(band[pos]);
    });
  });
  return kinds;
};

const EXTENSIONS = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };

/**
 * Default image upload: the editor's image pipeline (imageService: format and size checks,
 * resize to at most 3x the tallest band image, compression), then the file is stored in Quill's
 * Supabase storage and its public object URL is returned.
 * @param {File} file - Image file.
 * @returns {Promise<{src: string, alt: string}>} Image.
 */
export async function uploadPageSetupImage(file) {
  const [{ default: imageService }, { supabase }] = await Promise.all([
    import('../../services/imageService'),
    import('../../lib/supabase'),
  ]);
  const processed = await imageService.uploadImage(file, { maxWidth: 1200, maxHeight: IMAGE_MAX * 3 });
  const buffer = imageService.base64ToBuffer(processed.base64);
  if (!buffer) throw new Error('The image could not be read.');
  const mimeType = processed.mimeType || file.type || 'image/png';
  const blob = new Blob([buffer], { type: mimeType });
  const { data: auth, error: authError } = await supabase.auth.getUser();
  if (authError || !auth?.user) throw new Error('Sign in again to upload images.');
  const path = `${auth.user.id}/${processed.id}.${EXTENSIONS[mimeType] || 'png'}`;
  const { error } = await supabase.storage.from(PAGE_SETUP_IMAGE_BUCKET).upload(path, blob, {
    contentType: mimeType,
    cacheControl: '31536000',
    upsert: false,
  });
  if (error) {
    // The bucket comes with the page setup migration (20261105000100_page_setup.sql).
    if (/bucket not found/i.test(String(error.message || ''))) {
      throw new Error('Header and footer images are not available yet on this workspace. Use text for now.');
    }
    throw new Error(`The image could not be uploaded: ${error.message || 'storage error'}`);
  }
  const { data } = supabase.storage.from(PAGE_SETUP_IMAGE_BUCKET).getPublicUrl(path);
  return { src: data?.publicUrl || '', alt: String(file?.name || '').replace(/\.[a-z0-9]+$/i, '') };
}

/**
 * One text slot's text with "Insert field".
 * @param {{label: string, value: string, onChange: (text: string) => void}} props - Props.
 * @returns {JSX.Element}
 */
const SlotText = ({ label, value, onChange }) => {
  const inputRef = useRef(null);
  const selectionRef = useRef(null);
  /** Caret to put back in the input once the menu has closed after inserting a field. */
  const insertedCaretRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const focusInput = () => {
    const input = inputRef.current;
    const caret = insertedCaretRef.current;
    insertedCaretRef.current = null;
    if (!input || caret === null) return;
    input.focus();
    try {
      input.setSelectionRange(caret, caret);
    } catch {
      /* input type without selection */
    }
  };

  const insert = (token) => {
    const text = value || '';
    const saved = selectionRef.current;
    const start = saved ? saved.start : text.length;
    const end = saved ? saved.end : text.length;
    const next = `${text.slice(0, start)}${token}${text.slice(end)}`.slice(0, MAX_TEXT);
    const caret = Math.min(start + token.length, next.length);
    onChange(next);
    selectionRef.current = { start: caret, end: caret };
    // Closing returns focus to "Insert field" (MUI); once the menu is gone, focus moves on to
    // the input at the end of the inserted field (TransitionProps.onExited).
    insertedCaretRef.current = caret;
    setAnchor(null);
  };

  const remember = (event) => {
    const el = event.target;
    selectionRef.current = { start: el.selectionStart ?? (value || '').length, end: el.selectionEnd ?? (value || '').length };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <TextField
        size="small"
        label={`${label} text`}
        value={value || ''}
        inputRef={inputRef}
        onChange={(event) => {
          onChange(event.target.value.slice(0, MAX_TEXT));
          remember(event);
        }}
        onSelect={remember}
        onKeyUp={remember}
        onClick={remember}
        inputProps={{ maxLength: MAX_TEXT }}
        fullWidth
      />
      <Button
        size="small"
        variant="text"
        startIcon={<DataObject fontSize="small" />}
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : undefined}
        aria-label={`Insert field into ${label} text`}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0.75, minWidth: 0 }}
      >
        Insert field
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        TransitionProps={{ onExited: focusInput }}
        MenuListProps={{ dense: true, 'aria-label': 'Fields' }}
      >
        {FIELD_OPTIONS.map(({ token, label: fieldLabel }) => (
          <MenuItem key={token} onClick={() => insert(token)}>
            <Box component="span" sx={{ flex: 1 }}>{fieldLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ ml: 2, fontSize: 12, color: 'text.secondary', fontFamily: 'monospace' }}>{token}</Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

/**
 * One image slot: upload or replace, alt text and height.
 * @param {{label: string, slot: object, onChange: (slot: object) => void, uploadImage: (file: File) => Promise<{src: string, alt?: string}>, maxHeight?: number, marginIn?: number|null}} props - Props
 *   (`maxHeight`: tallest the margin can show, see bandImageMaxHeight).
 * @returns {JSX.Element}
 */
const SlotImage = ({ label, slot, onChange, uploadImage, maxHeight = IMAGE_MAX, marginIn = null }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const hasImage = Boolean(slot?.src);
  const height = Math.min(IMAGE_MAX, Math.max(IMAGE_MIN, Number(slot?.height) || DEFAULT_IMAGE_HEIGHT));
  const shrinkId = `${label}-image-fit`.replace(/\s+/g, '-');

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await uploadImage(file);
      if (!result?.src || !isAllowedImageUrl(result.src)) {
        setError('This image cannot be used in a header or footer. Try a PNG or JPEG file.');
        return;
      }
      onChange({ type: 'image', src: result.src, alt: slot?.alt || result.alt || '', height });
    } catch (err) {
      setError(err?.message || 'The image could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {hasImage && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px', p: 0.75, display: 'flex', justifyContent: 'center', bgcolor: 'background.default' }}>
          <Box component="img" src={slot.src} alt={slot.alt || ''} sx={{ maxWidth: '100%', height: Math.min(height, 48), objectFit: 'contain' }} />
        </Box>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={busy ? <CircularProgress size={14} /> : <UploadOutlined fontSize="small" />}
        disabled={busy}
        onClick={() => fileRef.current?.click()}
        aria-label={`${hasImage ? 'Replace' : 'Upload'} ${label} image`}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        {hasImage ? 'Replace image' : 'Upload image'}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        data-testid={`page-setup-file-${label}`}
        onChange={onFile}
      />
      {error && <Typography role="alert" sx={{ fontSize: 12, color: 'error.main' }}>{error}</Typography>}
      {hasImage && (
        <>
          <TextField
            size="small"
            label={`${label} alt text`}
            value={slot.alt || ''}
            onChange={(event) => onChange({ ...slot, alt: event.target.value.slice(0, IMAGE_ALT_MAX) })}
            inputProps={{ maxLength: IMAGE_ALT_MAX }}
            fullWidth
          />
          <Box>
            <Typography id={`${label}-height`.replace(/\s+/g, '-')} sx={{ fontSize: 12, color: 'text.secondary' }}>
              Height: {height} px
            </Typography>
            <Slider
              size="small"
              min={IMAGE_MIN}
              max={IMAGE_MAX}
              step={1}
              value={height}
              onChange={(_, next) => onChange({ ...slot, height: Array.isArray(next) ? next[0] : next })}
              getAriaValueText={(v) => `${v} px`}
              slotProps={{
                input: {
                  'aria-label': `${label} image height`,
                  ...(height > maxHeight ? { 'aria-describedby': shrinkId } : {}),
                },
              }}
            />
            {height > maxHeight && (
              <Typography id={shrinkId} sx={{ fontSize: 12, color: 'text.secondary' }}>
                {`Shrinks to fit the ${marginIn !== null ? `${marginIn} in ` : ''}margin (at most ${maxHeight} px).`}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * Editor for one slot (kind picker plus the matching controls).
 * @param {object} props - Props.
 * @returns {JSX.Element}
 */
const SlotEditor = ({ label, slot, kind, onKindChange, onChange, uploadImage, imageMaxHeight, marginIn }) => {
  const changeKind = (next) => {
    onKindChange(next);
    if (next === 'none') onChange(NONE);
    else if (next === 'text') onChange({ type: 'text', text: slot?.type === 'text' && slotKind(slot) === 'text' ? slot.text : '' });
    else if (next === 'page') onChange({ type: 'text', text: PAGE_NUMBER_FORMATS.n });
    else if (next === 'image') onChange(slot?.type === 'image' ? slot : { type: 'image', src: '', alt: '', height: DEFAULT_IMAGE_HEIGHT });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <TextField
        select
        size="small"
        label={label}
        value={kind}
        onChange={(event) => changeKind(event.target.value)}
        fullWidth
      >
        <MenuItem value="none">None</MenuItem>
        <MenuItem value="text">Text</MenuItem>
        <MenuItem value="image">Image</MenuItem>
        <MenuItem value="page">Page number</MenuItem>
      </TextField>
      {kind === 'text' && (
        <SlotText label={label} value={slot?.type === 'text' ? slot.text : ''} onChange={(text) => onChange({ type: 'text', text })} />
      )}
      {kind === 'page' && (
        <TextField
          select
          size="small"
          label={`${label} page number format`}
          value={String(slot?.text || '').trim() === PAGE_NUMBER_FORMATS.nOfM ? 'nOfM' : 'n'}
          onChange={(event) => onChange({ type: 'text', text: PAGE_NUMBER_FORMATS[event.target.value] })}
          fullWidth
        >
          <MenuItem value="n">3</MenuItem>
          <MenuItem value="nOfM">Page 3 of 8</MenuItem>
        </TextField>
      )}
      {kind === 'image' && (
        <SlotImage label={label} slot={slot} onChange={onChange} uploadImage={uploadImage} maxHeight={imageMaxHeight} marginIn={marginIn} />
      )}
    </Box>
  );
};

/**
 * Three slots of one band.
 * @param {object} props - Props.
 * @returns {JSX.Element}
 */
const BandEditor = ({ bandKey, band, kinds, onKindChange, onSlotChange, uploadImage, imageMaxHeight, marginIn }) => {
  const title = BAND_LABELS[bandKey];
  const value = bandOf(band);
  return (
    <Box component="fieldset" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', m: 0, px: 1.5, pt: 1.5, pb: 1.5, minWidth: 0 }}>
      <Box component="legend" sx={{ px: 0.5, fontSize: 13, fontWeight: 600 }}>{title}</Box>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' } }}>
        {POSITIONS.map((pos) => (
          <SlotEditor
            key={pos}
            label={`${title} ${POSITION_LABELS[pos]}`}
            slot={value[pos]}
            kind={kinds[`${bandKey}.${pos}`] || 'none'}
            onKindChange={(next) => onKindChange(`${bandKey}.${pos}`, next)}
            onChange={(slot) => onSlotChange(bandKey, pos, slot)}
            uploadImage={uploadImage}
            imageMaxHeight={imageMaxHeight}
            marginIn={marginIn}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Sample values for fields the preview has no data for.
 * @param {object} ctx - Field context.
 * @returns {object} Context with every field set.
 */
const previewContext = (ctx) => ({
  ...ctx,
  title: ctx?.title || 'Document title',
  customer: ctx?.customer || 'Customer',
  project: ctx?.project || 'Project',
  date: ctx?.date || formatFieldDate(new Date()),
});

/**
 * One band of the mini preview.
 * @param {object} props - Props.
 * @returns {JSX.Element}
 */
const PreviewBand = ({ band, height, side, ctx, scale, imageMaxHeight = IMAGE_MAX }) => {
  const value = bandOf(band);
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        [side]: 0,
        height,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        alignItems: 'center',
        columnGap: '4px',
      }}
    >
      {POSITIONS.map((pos) => {
        const slot = value[pos];
        let content = null;
        if (slot?.type === 'text' && slot.text) {
          content = renderSlotText(slot.text, ctx);
        } else if (slot?.type === 'image' && slot.src && isAllowedImageUrl(slot.src)) {
          content = (
            <Box
              component="img"
              src={slot.src}
              alt=""
              data-testid="preview-image"
              sx={{
                // Held to the band like the Pages view (margin less its edge and body insets).
                height: Math.max(4, Math.round(Math.min(Number(slot.height) || DEFAULT_IMAGE_HEIGHT, imageMaxHeight) * scale)),
                maxWidth: '100%',
                objectFit: 'contain',
                verticalAlign: 'middle',
              }}
            />
          );
        }
        return (
          <Box
            key={pos}
            data-testid={`preview-${side === 'top' ? 'header' : 'footer'}-${pos}`}
            sx={{
              minWidth: 0,
              textAlign: pos,
              fontSize: 7,
              lineHeight: 1.2,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {content}
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Screen reader summary of a band in the preview (the drawn sheet is hidden from them).
 * @param {object} band - Band.
 * @param {object} ctx - Field context with page and pages.
 * @returns {string} Summary, e.g. "left Acme logo image, centre 2".
 */
const describeBand = (band, ctx) => {
  const value = bandOf(band);
  const parts = POSITIONS.map((pos) => {
    const slot = value[pos];
    if (slot?.type === 'text' && slot.text.trim()) return `${POSITION_LABELS[pos]} ${renderSlotText(slot.text, ctx)}`;
    if (slot?.type === 'image' && slot.src) return `${POSITION_LABELS[pos]} ${slot.alt ? `${slot.alt} ` : ''}image`;
    return null;
  }).filter(Boolean);
  return parts.length ? parts.join(', ') : 'empty';
};

/**
 * A scaled sheet with its header and footer bands.
 * @param {object} props - Props.
 * @returns {JSX.Element}
 */
const MiniPage = ({ setup, pageNumber, pageCount, fieldContext, paperSize }) => {
  const geometry = resolvePageGeometry(setup, paperSize);
  const { width, height, margin } = geometry;
  const imageMaxHeight = bandImageMaxHeight(geometry);
  const scale = PREVIEW_MAX / Math.max(width, height);
  const m = { top: margin.top * scale, right: margin.right * scale, bottom: margin.bottom * scale, left: margin.left * scale };
  const bands = bandsForPage(setup, pageNumber);
  const ctx = { ...previewContext(fieldContext), page: pageNumber, pages: pageCount };

  return (
    <Box component="figure" sx={{ m: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box
        data-testid={`page-setup-preview-${pageNumber}`}
        aria-hidden="true"
        sx={{
          position: 'relative',
          width: Math.round(width * scale),
          height: Math.round(height * scale),
          bgcolor: 'var(--q-surface, #FFFFFF)',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '2px',
          boxShadow: '0 1px 4px rgba(28,27,25,0.12)',
          overflow: 'hidden',
          flex: 'none',
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: m.left, right: m.right }}>
          <PreviewBand band={bands.header} height={m.top} side="top" ctx={ctx} scale={scale} imageMaxHeight={imageMaxHeight} />
          <Box sx={{ position: 'absolute', top: m.top, bottom: m.bottom, left: 0, right: 0, border: '1px dashed', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: '5px', p: '6px' }}>
            {[92, 100, 84, 96, 70].map((w, i) => (
              <Box key={i} sx={{ height: 3, width: `${w}%`, bgcolor: 'action.selected', borderRadius: 1 }} />
            ))}
          </Box>
          <PreviewBand band={bands.footer} height={m.bottom} side="bottom" ctx={ctx} scale={scale} imageMaxHeight={imageMaxHeight} />
        </Box>
      </Box>
      <Typography component="figcaption" sx={{ fontSize: 12, color: 'text.secondary' }}>
        Page {pageNumber}
        <Box component="span" sx={visuallyHidden}>
          {`. Header: ${describeBand(bands.header, ctx)}. Footer: ${describeBand(bands.footer, ctx)}.`}
        </Box>
      </Typography>
    </Box>
  );
};

/**
 * @param {object} props - Props.
 * @param {boolean} props.open - Open state.
 * @param {() => void} props.onClose - Close without saving.
 * @param {object|null} props.value - Current page_setup (null = defaults).
 * @param {(next: object) => void} props.onSave - Receives the normalized page setup when it changed.
 * @param {{title?: string, customer?: string, project?: string, date?: string}} [props.fieldContext] - Live field values for the preview.
 * @param {'letter'|'a4'} [props.paperSize] - Paper size for the preview.
 * @param {{id?: string, name?: string}|null} [props.template] - The template a document came from ("From template").
 * @param {() => Promise<object|null>} [props.onLoadTemplatePageSetup] - Reads the template's current page_setup (Reset to template).
 * @param {(file: File) => Promise<{src: string, alt?: string}>} [props.uploadImage] - Image upload (default: the editor's image pipeline).
 * @param {string} [props.target] - 'document' or 'template' (helper text).
 * @returns {JSX.Element}
 */
const PageSetupDialog = ({
  open,
  onClose,
  value = null,
  onSave,
  fieldContext = null,
  paperSize = 'letter',
  template = null,
  onLoadTemplatePageSetup = null,
  uploadImage = uploadPageSetupImage,
  target = 'document',
}) => {
  const [draft, setDraft] = useState(() => toDraft(value));
  const [kinds, setKinds] = useState(() => kindsOf(toDraft(value)));
  const [resetState, setResetState] = useState({ busy: false, message: null, error: null });
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  // A fresh draft every time the dialog opens (not when the host re-renders while it is open).
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (!open) return;
    const next = toDraft(valueRef.current);
    setDraft(next);
    setKinds(kindsOf(next));
    setResetState({ busy: false, message: null, error: null });
  }, [open]);

  const setSlot = useCallback((bandKey, pos, slot) => {
    setDraft((prev) => ({ ...prev, [bandKey]: { ...bandOf(prev[bandKey]), [pos]: slot } }));
  }, []);
  const setKind = useCallback((key, kind) => setKinds((prev) => ({ ...prev, [key]: kind })), []);

  const normalized = useMemo(() => normalizePageSetup(draft), [draft]);
  const pageCount = Math.max(Number(fieldContext?.pages) || 0, draft.differentFirstPage ? 2 : 1, 3);

  const handleSave = () => {
    if (serializePageSetup(value) !== serializePageSetup(normalized)) onSave?.(normalized);
    onClose?.();
  };

  const handleReset = async () => {
    if (!onLoadTemplatePageSetup) return;
    setResetState({ busy: true, message: null, error: null });
    try {
      const fromTemplate = await onLoadTemplatePageSetup();
      const next = toDraft(fromTemplate || null);
      setDraft(next);
      setKinds(kindsOf(next));
      setResetState({ busy: false, message: "Reset to the template's current page setup. Save to apply it.", error: null });
    } catch (err) {
      setResetState({ busy: false, message: null, error: err?.message || 'The template could not be read.' });
    }
  };

  const marginIn = (key) => MARGIN_PRESETS[key].in;
  const imageMaxHeight = bandImageMaxHeight(resolvePageGeometry(normalized, paperSize));
  const bandProps = { kinds, onKindChange: setKind, onSlotChange: setSlot, uploadImage, imageMaxHeight, marginIn: marginIn(normalized.margins) };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isPhone} aria-labelledby="page-setup-title" aria-describedby="page-setup-description">
      <DialogTitle id="page-setup-title" sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Box sx={{ flex: 1 }}>Page setup</Box>
        <IconButton aria-label="Close" onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 260px' }, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
          <Typography id="page-setup-description" sx={{ fontSize: 13, color: 'text.secondary' }}>
            Applies to this {target === 'template' ? 'template and documents generated from it from now on' : 'document only'}. Headers and footers show in Pages view and in Word and PDF exports.
          </Typography>

          {template && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '8px', bgcolor: 'action.hover' }}>
              <Typography sx={{ fontSize: 13, flex: '1 1 200px', minWidth: 0 }} noWrap title={template.name || ''}>
                From template: <Box component="span" sx={{ fontWeight: 600 }}>{template.name || 'Untitled template'}</Box>
              </Typography>
              {onLoadTemplatePageSetup && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleReset}
                  disabled={resetState.busy}
                  startIcon={resetState.busy ? <CircularProgress size={14} /> : <RestartAlt fontSize="small" />}
                  sx={{ textTransform: 'none' }}
                >
                  Reset to template
                </Button>
              )}
              {resetState.message && <Typography role="status" sx={{ fontSize: 12, color: 'text.secondary', flexBasis: '100%' }}>{resetState.message}</Typography>}
              {resetState.error && <Typography role="alert" sx={{ fontSize: 12, color: 'error.main', flexBasis: '100%' }}>{resetState.error}</Typography>}
            </Box>
          )}

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <FormControl>
              <FormLabel id="page-setup-orientation" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>Orientation</FormLabel>
              <RadioGroup
                aria-labelledby="page-setup-orientation"
                value={draft.orientation}
                onChange={(event) => setDraft((prev) => ({ ...prev, orientation: event.target.value }))}
              >
                <FormControlLabel value="portrait" control={<Radio size="small" />} label="Portrait" />
                <FormControlLabel value="landscape" control={<Radio size="small" />} label="Landscape" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel id="page-setup-margins" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>Margins</FormLabel>
              <RadioGroup
                aria-labelledby="page-setup-margins"
                value={draft.margins}
                onChange={(event) => setDraft((prev) => ({ ...prev, margins: event.target.value }))}
              >
                {MARGIN_ORDER.map((key) => (
                  <FormControlLabel
                    key={key}
                    value={key}
                    control={<Radio size="small" />}
                    label={`${MARGIN_LABELS[key]} (${marginIn(key)} in)`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <BandEditor bandKey="header" band={draft.header} {...bandProps} />
          <BandEditor bandKey="footer" band={draft.footer} {...bandProps} />

          <Box>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(draft.differentFirstPage)}
                  onChange={(event) => setDraft((prev) => ({ ...prev, differentFirstPage: event.target.checked }))}
                  inputProps={{ 'aria-describedby': 'page-setup-first-page-help' }}
                />
              )}
              label="Different first page"
            />
            <Typography id="page-setup-first-page-help" sx={{ fontSize: 12, color: 'text.secondary', ml: 6, mt: -0.5 }}>
              The first page gets its own header and footer, set below. Leave them empty for a clean cover page.
            </Typography>
          </Box>
          {draft.differentFirstPage && (
            <>
              <BandEditor bandKey="firstHeader" band={draft.firstHeader} {...bandProps} />
              <BandEditor bandKey="firstFooter" band={draft.firstFooter} {...bandProps} />
            </>
          )}
        </Box>

        <Box
          component="section"
          aria-label="Preview"
          sx={{
            // On phones (one column) the preview comes first, above the slot editors.
            order: { xs: -1, md: 0 },
            position: { md: 'sticky' },
            top: 0,
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, width: '100%', textAlign: 'center' }}>Preview</Typography>
          <MiniPage setup={normalized} pageNumber={1} pageCount={pageCount} fieldContext={fieldContext} paperSize={paperSize} />
          {normalized.differentFirstPage && (
            <MiniPage setup={normalized} pageNumber={2} pageCount={pageCount} fieldContext={fieldContext} paperSize={paperSize} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageSetupDialog;
